import { Box, Chip, IconButton, Skeleton, Typography } from '@mui/material';
import { ReactComponent as CalendarIcon } from 'assets/calendar.svg';
import { ReactComponent as WarningOrangeIcon } from 'assets/error-orange.svg';
import { ReactComponent as WarningRedIcon } from 'assets/error-red.svg';
import CustomOverlapImg from 'components/shared/CustomOverlapImg/CustomOverlapImg';
import { memo, MouseEvent, useState } from 'react';
import zeplinColor from 'theme/Color';
import { Constants } from 'utilities/constants';
import { Utilities } from 'utilities/utilities';
import { TasksSettings } from '../../TasksSettings';

type TaskCardProps = {
  data: {
    id?: string;
    subject?: string;
    priority?: string;
    assignedTo?:
      | string
      | Array<{ Name: string; Id: string; avatar?: string; profile_image_url?: string }>;
    assignedToName?: string;
    avatar?: string;
    dueDate?: string;
    isOverdue?: boolean;
    status?: string;
    link?: string;
    source?: string;
    taskHealth?: 'onTrack' | 'warning' | 'overdue';
    [key: string]: any;
  };
  loading?: boolean;
  onClick: () => void;
  onViewDetails: () => void;
  onEdit: () => void;
  onStatusChange: () => void;
  onDelete: () => void;
  onDrag?: (e: React.DragEvent, taskId: string, sourceColumn: string) => void;
  tabValue?: number;
};

export const TaskCard = memo(
  ({
    data,
    loading = false,
    onClick,
    onViewDetails,
    onEdit,
    onStatusChange,
    onDelete,
    onDrag,
    tabValue,
  }: TaskCardProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const getInitials = (name?: string) => {
      if (!name || typeof name !== 'string') return '';
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const getPriorityColor = (priority?: string) => {
      const priorityValue = priority || 'Medium';
      const priorityKey = priorityValue === 'Medium' ? 'Normal' : priorityValue;
      return (
        Constants.PriorityTask[priorityKey as 'Normal' | 'Low' | 'High'] ||
        Constants.PriorityTask.Normal
      );
    };

    const priorityColor = getPriorityColor(data?.priority);

    const getCardColor = (): string => {
      const health = data?.taskHealth;
      const healthLower = health ? String(health).toLowerCase() : '';

      if (healthLower === 'overdue') {
        return zeplinColor.Error;
      }

      if (healthLower === 'warning') {
        return zeplinColor.AlertInfo;
      }

      return zeplinColor.Neutral5;
    };

    const borderColor = getCardColor();

    if (loading) {
      return (
        <Box
          sx={{
            borderRadius: '14px',
            backgroundColor: zeplinColor.Neutral1,
            border: `1px solid ${zeplinColor.Neutral5}`,
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Skeleton variant='text' width='70%' height={24} />
            <Skeleton variant='circular' width={24} height={24} />
          </Box>
          <Box>
            <Skeleton variant='rounded' width={60} height={24} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Skeleton variant='circular' width={32} height={32} />
              <Skeleton variant='text' width={100} height={20} />
            </Box>
            <Skeleton variant='text' width={100} height={20} />
          </Box>
        </Box>
      );
    }

    const handleDragStart = (e: React.DragEvent) => {
      if (onDrag && data?.id && data?.status) {
        onDrag(e, data.id, data.status);
      }
    };

    return (
      <Box
        sx={{
          borderRadius: '14px',
          backgroundColor:
            data?.status?.toLowerCase() === 'done' ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
          border: `1px solid ${borderColor}`,
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            padding: '15px',
            border: `2px solid ${borderColor}`,
          },
        }}
        onClick={onClick}
        draggable={onDrag ? 'true' : undefined}
        onDragStart={handleDragStart}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography
            variant='responsiveBody2'
            sx={{
              fontWeight: 500,
              color: zeplinColor.BasicBlack,
              flex: 1,
              lineHeight: 1.4,
            }}
          >
            {data?.subject || 'No Subject'}
          </Typography>
          <IconButton
            size='small'
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: zeplinColor.Neutral4,
              },
            }}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
          >
            <img src='/assets/vertical-dots.svg' width={20} height={20} alt='Menu' />
          </IconButton>
        </Box>

        {data?.priority && (
          <Box>
            <Chip
              label={
                <Typography variant='responsiveLabel1' sx={{ fontWeight: 500 }}>
                  {data.priority}
                </Typography>
              }
              sx={{
                bgcolor: priorityColor,
                color: zeplinColor.Neutral1,
                height: '24px',
                padding: '4px 12px',
                borderRadius: '50px',
                '& .MuiChip-label': {
                  padding: 0,
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {(() => {
              let assigneesArray: Array<{
                Id: string;
                Name: string;
                Email?: string;
                profile_image_url?: string;
                avatar?: string;
              }> | null = null;

              if (data?.assignedTo && Array.isArray(data.assignedTo)) {
                assigneesArray = (data.assignedTo as Array<any>)
                  .filter(
                    (assignee: any) =>
                      assignee && typeof assignee === 'object' && assignee?.Id && assignee?.Name,
                  )
                  .map((assignee: any) => ({
                    Id: assignee.Id,
                    Name: assignee.Name,
                    Email: assignee?.Email,
                    profile_image_url: assignee?.profile_image_url,
                    avatar: assignee?.avatar || assignee?.profile_image_url,
                  }));
              }

              if (
                (!assigneesArray || assigneesArray.length === 0) &&
                data?.assignedTo &&
                Array.isArray(data.assignedTo)
              ) {
                assigneesArray = data.assignedTo
                  .filter((assignee: any) => assignee?.Id && assignee?.Name)
                  .map((assignee: any) => ({
                    Id: assignee.Id,
                    Name: assignee.Name,
                    Email: assignee?.Email,
                    profile_image_url: assignee?.profile_image_url,
                    avatar: assignee?.avatar || assignee?.profile_image_url,
                  }));
              }

              if (assigneesArray && assigneesArray.length > 1) {
                const images = assigneesArray.map(
                  (assignee) => assignee?.profile_image_url || assignee?.avatar || '',
                );
                const names = assigneesArray.map((assignee) => assignee?.Name || '');
                return <CustomOverlapImg images={images} name={names} maxVisible={3} />;
              }

              const singleAssignee =
                assigneesArray && assigneesArray.length === 1 ? assigneesArray[0] : null;

              const avatar =
                singleAssignee?.profile_image_url || singleAssignee?.avatar || data?.avatar;
              const assignedToName = singleAssignee?.Name || data?.assignedToName;

              if (avatar) {
                return (
                  <>
                    <Box
                      sx={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: zeplinColor.Neutral5,
                      }}
                    >
                      <img
                        src={avatar}
                        alt={assignedToName || 'Assignee'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                    {assignedToName && (
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        {assignedToName}
                      </Typography>
                    )}
                  </>
                );
              } else if (assignedToName && typeof assignedToName === 'string') {
                return (
                  <>
                    <Box
                      sx={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: zeplinColor.Brand1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant='label2' color={zeplinColor.Neutral1}>
                        {getInitials(assignedToName)}
                      </Typography>
                    </Box>
                    <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                      {assignedToName}
                    </Typography>
                  </>
                );
              } else {
                return (
                  <Box
                    sx={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: zeplinColor.Neutral5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img src='/assets/no-assignee.png' width={32} height={32} alt='No assignee' />
                  </Box>
                );
              }
            })()}
          </Box>

          {data?.dueDate &&
            (() => {
              const health = data?.taskHealth;
              const healthLower = health ? String(health).toLowerCase() : '';
              const isOverdue = healthLower === 'overdue' || data?.isOverdue;
              const isWarning = healthLower === 'warning';

              return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <CalendarIcon />
                  <Typography
                    variant='responsiveBody3'
                    sx={{
                      color: isOverdue
                        ? zeplinColor.Error
                        : isWarning
                          ? zeplinColor.AlertInfo
                          : zeplinColor.BasicBlack,
                    }}
                  >
                    {Utilities.formatDate(data.dueDate)}
                  </Typography>
                  {isOverdue && <WarningRedIcon />}
                  {isWarning && !isOverdue && <WarningOrangeIcon />}
                </Box>
              );
            })()}
        </Box>

        <TasksSettings
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          task={data}
          onViewDetails={onViewDetails}
          onEdit={onEdit}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
          tabValue={tabValue}
        />
      </Box>
    );
  },
);
